import * as React from "react";
import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  type Placement,
} from "@floating-ui/react";

import { usePortalScopeClassName } from "@/design-system/floating/usePortalScope";

import "./tooltip.css";

type TooltipSide = "top" | "right" | "bottom" | "left";
type TooltipAlign = "start" | "center" | "end";

export interface TooltipProps {
  children: React.ReactElement;
  content: React.ReactNode;
  side?: TooltipSide;
  align?: TooltipAlign;
  placement?: Placement;
  /** Milliseconds before the tooltip opens on hover. `delayDuration` is the historical alias. */
  enterDelay?: number;
  delayDuration?: number;
  className?: string;
  /** Applied to the tooltip bubble — the portalled box — not to the trigger. */
  style?: React.CSSProperties;
}

function getPlacement(side: TooltipSide, align: TooltipAlign): Placement {
  return (align === "center" ? side : `${side}-${align}`) as Placement;
}

/**
 * Our tooltip, built on `@floating-ui/react`.
 *
 * ── WHY THIS IS OURS NOW ──────────────────────────────────────────────────────────────
 *
 * `@floating-ui/react` was ALREADY in the dependency tree — Salt is built on it, so the
 * accessibility layer this component needs (`useRole`, `useDismiss`, `useFocus`, `useHover`,
 * and the positioning engine) was installed and paid for before this change. Owning the tooltip
 * therefore adds no third-party surface; it removes a layer.
 *
 * ── WHAT IT DELETES ───────────────────────────────────────────────────────────────────
 *
 * The previous wrapper was 115 lines, most of them a workaround. Salt's `Tooltip` forwards
 * `...rest` into `useTooltip`, which reads eight named options and DISCARDS the rest, so a
 * `style` prop on `<Tooltip>` reached no DOM node. Reaching the bubble meant swapping the whole
 * `FloatingComponentProvider` for a memoised wrapper component that merged the style, with the
 * latest value read from a ref so the portalled bubble would not remount and drop an open
 * tooltip mid-hover. All of that existed to set `max-width` on a box.
 *
 * Here the bubble is ours, so `style` is just a prop on it.
 *
 * ── THE CONTRACT THAT IS DELIBERATELY UNCHANGED ───────────────────────────────────────
 *
 * `role="tooltip"`, the `saltTooltip` class and `data-placement` are all asserted by
 * `tooltip.test.tsx` across 82 call sites' worth of behaviour. `saltTooltip` is a legacy name
 * for a vendor that no longer renders this — kept so the swap is invisible, and renamed later
 * on its own, where a mistake is obvious.
 *
 * `strategy: "absolute"` matches Salt's, because the test asserts `position: absolute` and a
 * fixed-strategy tooltip behaves differently inside scrolling containers.
 */
const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(function Tooltip(
  { align = "center", children, className, content, delayDuration, enterDelay, placement, side = "top", style },
  ref,
) {
  const [open, setOpen] = React.useState(false);
  const [referenceEl, setReferenceEl] = React.useState<Element | null>(null);

  const { refs, floatingStyles, context, placement: resolvedPlacement } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: placement ?? getPlacement(side, align),
    strategy: "absolute",
    // `autoUpdate` keeps the bubble attached while the page scrolls or the trigger moves.
    // Without it a tooltip opened inside a scrolling table detaches from its cell.
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
  });

  const hover = useHover(context, {
    // The historical prop name is `delayDuration`; `enterDelay` is Salt's. Both are honoured
    // because both appear at call sites today.
    delay: { open: enterDelay ?? delayDuration ?? 0, close: 0 },
    move: false,
  });
  // Focus matters as much as hover: a tooltip only reachable by pointer is not reachable.
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);
  const portalScope = usePortalScopeClassName(referenceEl);

  return (
    <>
      {React.cloneElement(
        children,
        getReferenceProps({
          ref: (node: HTMLElement | null) => {
            // Tracked in state so the portal scope can react to it — the density and theme
            // classes are read from the TRIGGER's provider ancestor.
            setReferenceEl(node);
            refs.setReference(node);
          },
          ...(children.props as Record<string, unknown>),
        }),
      )}
      {open ? (
        <FloatingPortal>
          {/*
            Scope wrapper. It is INERT in this app — the density classes live on <html>, so a
            portal inherits them already — and exists for the case where a page nests a
            provider, when a tooltip should adopt the scope of whatever opened it.
            It is NOT fixing a regression: measurement showed this bubble already matched the
            page. See `@/design-system/floating/usePortalScope` for what it did show.
          */}
          <div className={portalScope}>
          <div
            {...getFloatingProps({
              ref: (node: HTMLDivElement | null) => {
                refs.setFloating(node);
                if (typeof ref === "function") ref(node);
                else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
              },
              className: className ? `saltTooltip ds-tooltip ${className}` : "saltTooltip ds-tooltip",
              style: { ...floatingStyles, ...style },
            })}
            data-placement={resolvedPlacement}
          >
            {content}
          </div>
          </div>
        </FloatingPortal>
      ) : null}
    </>
  );
});

export { Tooltip };
